/**
 * BackendUrlResolver - Production-Safe Backend URL Detection
 * date_of_change: 2025-09-26
 *
 * Production-safe backend URL resolution:
 * - Pin to Railway in production (no localhost probing)
 * - Allow localhost discovery in development
 * - Respect environment variable overrides
 * - Prevent mixed-content security issues
 */

// Production host suffixes for automatic production detection
const PROD_HOST_SUFFIXES = ['vercel.app', 'netlify.app', 'herokuapp.com'];
const DEBUG_ENABLED = typeof process !== 'undefined'
  ? !!(process.env.DEBUG_WINDSURF || process.env.DEBUG || (process.env.NODE_ENV === 'development' && process.env.REACT_APP_DEBUG_LOGS))
  : (typeof window !== 'undefined' && (window.DEBUG_WINDSURF === true || window.DEBUG === true));

const debugLog = (...args) => {
  if (DEBUG_ENABLED) {
    console.debug(...args);
  }
};

class BackendUrlResolver {
  constructor() {
    this.cache = {
      url: null,
      timestamp: null,
      ttl: 5 * 60 * 1000 // 5 minutes
    };

    // Use getCachedBackend for production-safe cache loading
    const cachedUrl = this._getCachedBackend();
    if (cachedUrl) {
      this.cache.url = cachedUrl;
      this.cache.timestamp = Date.now(); // Refresh timestamp on load
    }

    // Priority order with production-safe URL resolution
    // @date_of_change: 2025-09-26 - Added production host detection and localhost blocking
    const rawUrls = this._getPrioritizedUrls();

    // Remove duplicates while preserving order
    this.candidateUrls = [...new Set(rawUrls)];
  }

  _saveCache() {
    try {
      window.localStorage.setItem(
        'backend_url_cache',
        JSON.stringify({ url: this.cache.url, timestamp: this.cache.timestamp })
      );
    } catch {}
  }

  /**
   * Detect if we're running in a production environment
   * @date_of_change: 2025-09-26 - Added production host detection
   */
  _isProductionHost() {
    if (typeof window === 'undefined') return false;

    const hostname = window.location?.hostname || '';

    // Check for known production hosts
    const isProdHost =
      hostname === 'virtual-combat-simulator-x1tf.vercel.app' ||
      PROD_HOST_SUFFIXES.some(suffix => hostname.endsWith(suffix));

    return isProdHost;
  }

  /**
   * Detect if we're running over HTTPS (production indicator)
   * @date_of_change: 2025-09-26 - Added HTTPS detection
   */
  _isHttps() {
    return typeof window !== 'undefined' && window.location?.protocol === 'https:';
  }

  /**
   * Get cached backend URL with production safety checks
   * Ignore localhost cache in production to prevent split-brain scenarios
   * @date_of_change: 2025-09-26 - Enhanced production detection with host checking
   */
  _getCachedBackend() {
    const keyCandidates = ['backend_url_cache', 'backendUrl', 'backend_url', 'vcs:backendUrl'];
    for (const key of keyCandidates) {
      const stored = window.localStorage.getItem(key);
      if (!stored) continue;

      try {
        let url;
        if (key === 'backend_url_cache') {
          const parsed = JSON.parse(stored);
          url = parsed.url;
        } else {
          url = stored;
        }

        if (!url) continue;

        // Check if URL is localhost/127.0.0.1
        const isLocal = /^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?/i.test(url);
        const isProd = this._isProductionHost() || this._isHttps();

        if (isProd && isLocal) {
          // Don't trust local URLs in production - clear them
          debugLog('[BackendUrlResolver] Ignoring localhost cache in production:', url);
          window.localStorage.removeItem(key);
          continue;
        }

        return url;
      } catch {
        // Invalid JSON or other error, continue
      }
    }
    return null;
  }

  clearCache() {
    this.cache.url = null;
    this.cache.timestamp = null;
    // Clear all possible cache keys
    const keyCandidates = ['backend_url_cache', 'backendUrl', 'backend_url', 'vcs:backendUrl'];
    keyCandidates.forEach(key => {
      try { window.localStorage.removeItem(key); } catch {}
    });
  }

  /**
   * Force clear cache and prioritize localhost for development
   * @date_of_change: 2025-09-21 - Added for development environment reset
   */
  forceLocalhost() {
    this.clearCache();
    // Remove any non-localhost URLs from candidate list temporarily
    this.candidateUrls = [
      'http://localhost:5000',
      'http://127.0.0.1:5000'
    ];
    debugLog('[BackendUrlResolver] Forced localhost-only mode');
  }

  /**
   * Force clear cache and prioritize Railway for production reconnection
   * @date_of_change: 2025-09-24 - Added for Railway-first reconnection
   */
  forceRailway() {
    this.clearCache();
    // Reset to full candidate list with Railway priority
    const rawUrls = [
      process.env.REACT_APP_RAILWAY_URL, // Railway production URL (highest priority)
      process.env.REACT_APP_API_URL, // Generic API URL
      process.env.REACT_APP_BACKEND_URL, // Legacy backend URL
      'http://localhost:5000', // Local development
      process.env.REACT_APP_NGROK_URL // ngrok as last resort
    ];
    this.candidateUrls = rawUrls.filter(url => url && url.trim() !== '');
    debugLog('[BackendUrlResolver] Reset to Railway-first priority:', this.candidateUrls);
  }

  /**
   * Get prioritized URLs with production-safe environment variable precedence
   * @date_of_change: 2025-09-26 - Production-safe URL resolution with localhost blocking
   */
  _getPrioritizedUrls() {
    const isProd = this._isProductionHost() || this._isHttps();
    const envBackend =
      process.env.REACT_APP_API_URL ||
      process.env.REACT_APP_BACKEND_URL ||
      process.env.REACT_APP_RAILWAY_URL;

    // If we're on a production host and have a configured backend, pin to it (no localhost probing)
    if (isProd && envBackend) {
      debugLog('[BackendUrlResolver] Production host detected - pinning to environment backend:', envBackend);
      return [envBackend];
    }

    // If environment variable is set, use it as single source of truth
    if (envBackend) {
      debugLog('[BackendUrlResolver] Using environment backend URL:', envBackend);
      return [envBackend];
    }

    // Development mode: allow localhost discovery but Railway first for fallback
    const rawUrls = [
      process.env.REACT_APP_RAILWAY_URL, // Railway production URL (fallback)
      process.env.REACT_APP_API_URL, // Generic API URL
    ];

    // Only add localhost URLs in development (never in production)
    if (!isProd && !process.env.REACT_APP_DISABLE_LOCALHOST_PROBE) {
      rawUrls.push(
        'http://localhost:5000', // Local development
        'http://127.0.0.1:5000', // Local development fallback
      );
      debugLog('[BackendUrlResolver] Development mode - including localhost URLs');
    } else if (isProd) {
      debugLog('[BackendUrlResolver] Production mode - blocking localhost probe for security');
    }

    // Add ngrok as last resort in development only
    if (!isProd && process.env.REACT_APP_NGROK_URL) {
      rawUrls.push(process.env.REACT_APP_NGROK_URL);
    }

    return rawUrls.filter(Boolean);
  }

  async getBackendUrl() {
    // Always check for fresh environment variables on each call
    const freshCandidates = this._getPrioritizedUrls();

    // If we have a single source of truth from env vars, use it directly
    if (freshCandidates.length === 1) {
      const envUrl = freshCandidates[0];
      debugLog('[BackendUrlResolver] Using single source of truth:', envUrl);
      return envUrl;
    }

    // Use cache only if it's valid and not stale
    const cachedUrl = this._getCachedBackend();
    if (cachedUrl && this.cache.timestamp &&
        (Date.now() - this.cache.timestamp) < this.cache.ttl) {
      debugLog('[BackendUrlResolver] Using cached backend:', cachedUrl);
      return cachedUrl;
    }

    debugLog('[BackendUrlResolver] Searching for available backend...');
    this.candidateUrls = [...new Set(freshCandidates)];
    const workingUrl = await this.findWorkingBackendUrl();

    if (workingUrl) {
      this.cache.url = workingUrl;
      this.cache.timestamp = Date.now();
      this._saveCache();
      debugLog('[BackendUrlResolver] Found working backend:', workingUrl);
      return workingUrl;
    }

    // last resort with warning
    const fallbackUrl = this.candidateUrls[0] || 'http://localhost:5000';
    console.warn('[BackendUrlResolver] No working backend found, using fallback:', fallbackUrl);
    console.warn('[BackendUrlResolver] Tried URLs:', this.candidateUrls);
    return fallbackUrl;
  }

  async findWorkingBackendUrl() {
    for (const base of this.candidateUrls) {
      debugLog(`[BackendUrlResolver] Testing: ${base}`);
      if (await this.testBackendUrl(base)) {
        debugLog(`[BackendUrlResolver] ✓ Available: ${base}`);
        return base;
      } else {
        debugLog(`[BackendUrlResolver] ✗ Failed: ${base}`);
      }
    }
    return null;
  }

  async testBackendUrl(base) {
    // Try /health, /api/health, then /api/verify-beta as fallbacks
    const candidates = [
      { url: `${base}/health`, headers: { 'Accept': 'application/json' } },
      { url: `${base}/api/health`, headers: { 'Accept': 'application/json' } },
      { url: `${base}/api/verify-beta`, headers: { 'Accept': 'application/json', 'ngrok-skip-browser-warning': 'true' } }
    ];

    for (const { url, headers } of candidates) {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 3000);

        const res = await fetch(url, {
          method: 'GET',
          // no credentials for health check
          mode: 'cors',
          signal: controller.signal,
          headers
        });

        clearTimeout(timeoutId);

        // Accept any response for health/api endpoints, or 405/403 for verify-beta endpoint
        if (res.ok || (url.includes('verify-beta') && (res.status === 405 || res.status === 403))) {
          console.log(`[BackendUrlResolver] Backend detected at ${base} via ${url} (status: ${res.status})`);
          return true;
        }
      } catch {
        // ignore and try next
      }
    }
    return false;
  }

  addCandidateUrl(url) {
    if (url && !this.candidateUrls.includes(url)) {
      this.candidateUrls.unshift(url);
      this.clearCache();
    }
  }

  /**
   * Get diagnostic information for troubleshooting
   * @date_of_change: 2025-09-21 - Added for better error reporting
   */
  /**
   * Force clear all cached data and reset environment variables
   * @date_of_change: 2025-09-24 - Added for immediate cache reset
   */
  forceReset() {
    this.clearCache();
    // Reload environment variables with fresh priority
    this.candidateUrls = [...new Set(this._getPrioritizedUrls())];
    console.log('[BackendUrlResolver] Complete reset - Environment URLs:', {
      railway: process.env.REACT_APP_RAILWAY_URL,
      api: process.env.REACT_APP_API_URL,
      backend: process.env.REACT_APP_BACKEND_URL,
      ngrok: process.env.REACT_APP_NGROK_URL,
      candidates: this.candidateUrls
    });
  }

  /**
   * Convert HTTP URL to WebSocket URL with proper protocol handling
   * @date_of_change: 2025-09-25 - Added for WebSocket consistency
   */
  httpToWs(url) {
    if (!url) return null;
    return url.replace(/^http:/, 'ws:').replace(/^https:/, 'wss:');
  }

  /**
   * Get WebSocket URL based on current backend URL
   * Ensures WebSocket uses same base as REST API
   * @date_of_change: 2025-09-25 - Added for WebSocket/REST consistency
   */
  async getWebSocketUrl() {
    const backendUrl = await this.getBackendUrl();
    const wsUrl = this.httpToWs(backendUrl);
    console.log('[BackendUrlResolver] WebSocket URL:', wsUrl, 'from backend:', backendUrl);
    return wsUrl;
  }

  async getDiagnostics() {
    const diagnostics = {
      candidateUrls: this.candidateUrls,
      cache: { ...this.cache },
      workingUrl: null,
      failedUrls: [],
      timestamp: new Date().toISOString()
    };

    // Test all URLs without caching
    for (const base of this.candidateUrls) {
      const isWorking = await this.testBackendUrl(base);
      if (isWorking) {
        diagnostics.workingUrl = base;
      } else {
        diagnostics.failedUrls.push(base);
      }
    }

    return diagnostics;
  }

  /**
   * Force refresh the backend URL cache and search for available backends
   * @date_of_change: 2025-09-21 - Added for manual refresh capability
   */
  async refresh() {
    this.clearCache();
    return await this.getBackendUrl();
  }

  /**
   * Set a custom backend URL and test connectivity
   * @date_of_change: 2025-09-21 - Added for manual URL override
   */
  async setCustomUrl(url) {
    if (!url || typeof url !== 'string') {
      throw new Error('Invalid URL provided');
    }

    // Clean and validate the URL
    let cleanUrl = url.trim();
    if (!cleanUrl.startsWith('http://') && !cleanUrl.startsWith('https://')) {
      cleanUrl = 'https://' + cleanUrl;
    }

    // Remove trailing slash
    cleanUrl = cleanUrl.replace(/\/$/, '');

    console.log(`[BackendUrlResolver] Testing custom URL: ${cleanUrl}`);

    // Test if the custom URL works
    const isWorking = await this.testBackendUrl(cleanUrl);
    if (!isWorking) {
      throw new Error(`Custom backend URL ${cleanUrl} is not reachable`);
    }

    // Add to the front of candidate URLs and update cache
    this.candidateUrls.unshift(cleanUrl);
    this.cache.url = cleanUrl;
    this.cache.timestamp = Date.now();
    this._saveCache();

    console.log(`[BackendUrlResolver] Custom URL set successfully: ${cleanUrl}`);
    return cleanUrl;
  }
}

const backendUrlResolver = new BackendUrlResolver();

/**
 * Production-safe backend URL getter following user specifications
 * @date_of_change: 2025-09-26 - Implemented user-specified production safety pattern
 */
export function getBackendBase() {
  const envBase = process.env.REACT_APP_API_URL || process.env.REACT_APP_BACKEND_URL; // Environment variable override
  const host = (typeof window !== 'undefined' && window.location?.hostname) || '';
  const protocol = (typeof window !== 'undefined' && window.location?.protocol) || '';
  const isHttps = protocol === 'https:';

  const isProdHost =
    host &&
    (host === 'virtual-combat-simulator-x1tf.vercel.app' ||
     PROD_HOST_SUFFIXES.some(suf => host.endsWith(suf)));

  const isProductionContext = isProdHost || isHttps;

  // If we're on a production host and have a configured backend, pin to it.
  if (isProductionContext && envBase) {
    console.log('[getBackendBase] Production host detected - pinning to:', envBase);
    return envBase.replace(/\/+$/, '');
  }

  // DEV: use discovery logic (localhost probe, etc.) but guard against production
  if (isProductionContext) {
    // In production without env var, use Railway as fallback (never localhost)
    const fallback = 'https://virtualcombatsimulator-production.up.railway.app';
    console.warn('[getBackendBase] Production host without env var - using Railway fallback:', fallback);
    return fallback;
  }

  // Development: use existing discovery logic
  return backendUrlResolver.getBackendUrl().then(url => url.replace(/\/+$/, ''));
}

// Legacy compatibility - async version that returns the resolver's URL
export async function discoverBackendBase() {
  const url = await backendUrlResolver.getBackendUrl();
  return url.replace(/\/+$/, '');
}

export default backendUrlResolver;
